import { useEffect, useMemo, useState, FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { getPublicStatus, verifyPagePasscode } from '../../api/public';
import { Spinner } from '../common/Spinner';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Alert } from '../common/Alert';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { formatDateTime, formatDate, formatTime, parseServerDate } from '../../utils/datetime';
import { statusColors, statusLabels, formatUptime, formatDuration } from '../../utils/status';
import { apiError } from '../../utils/apiError';
import { DailyCell, PublicIncident, PublicStatus, PublicStatusService, ServiceStatus } from '../../types';

const banner: Record<ServiceStatus, { text: string; band: string }> = {
  up:          { text: 'All systems operational', band: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-900' },
  degraded:    { text: 'Degraded performance',    band: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-900' },
  down:        { text: 'Service disruption',      band: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-900' },
  maintenance: { text: 'Under maintenance',       band: 'bg-brand-50 dark:bg-brand-900/20 border-brand-200 dark:border-brand-900' },
  pending:     { text: 'Awaiting first checks',   band: 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700' },
};

const UPDATE_LABEL: Record<string, string> = {
  investigating: 'Investigating',
  identified: 'Identified',
  monitoring: 'Monitoring',
  resolved: 'Resolved',
};

/** What the API said when it refused: which kind of proof it wants. */
type Refusal = { status: number; access?: 'passcode' | 'authenticated'; error?: string };

function refusalOf(err: unknown): Refusal | null {
  if (!axios.isAxiosError(err) || !err.response) return null;
  return { status: err.response.status, access: err.response.data?.access, error: err.response.data?.error };
}

interface Props {
  /** Compact variant for iframes: banner and rows only. */
  embed?: boolean;
}

export function StatusPage({ embed = false }: Props) {
  const { slug } = useParams<{ slug: string }>();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['public-status', slug ?? ''],
    queryFn: () => getPublicStatus(slug),
    refetchInterval: 60_000,
    retry: false,
  });

  useDocumentTitle(data?.page.title ?? 'Status');
  useBranding(data);

  const refusal = error ? refusalOf(error) : null;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Spinner size="lg" label="Loading status…" />
      </div>
    );
  }

  if (refusal?.status === 401 && refusal.access === 'passcode') {
    return (
      <Shell embed={embed}>
        <PasscodePrompt onVerified={() => qc.invalidateQueries({ queryKey: ['public-status', slug ?? ''] })} />
      </Shell>
    );
  }

  if (refusal?.status === 401 && refusal.access === 'authenticated') {
    return (
      <Shell embed={embed}>
        <div className="text-center max-w-sm mx-auto space-y-3">
          <h1 className="text-xl font-semibold">Sign in to view this page</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">This status page is only shown to signed-in users.</p>
          <Link to="/login" state={{ from: `/status/${slug ?? ''}` }}>
            <Button>Sign in</Button>
          </Link>
        </div>
      </Shell>
    );
  }

  // A 404 here means the operator has not published this page. Say so plainly
  // rather than implying something is broken.
  if (error || !data) {
    return (
      <Shell embed={embed}>
        <div className="text-center max-w-sm mx-auto">
          <h1 className="text-xl font-semibold">No status page here</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            {slug ? `Nothing is published at /status/${slug}.` : 'This PulseCheck instance has not published one.'}
          </p>
        </div>
      </Shell>
    );
  }

  return <StatusPageBody data={data} embed={embed} />;
}

/** Page chrome shared by every state, so the branded accent applies to the passcode prompt too. */
function Shell({ children, embed }: { children: React.ReactNode; embed: boolean }) {
  return (
    <div className={`status-page min-h-screen bg-gray-50 dark:bg-gray-900 ${embed ? 'py-4' : ''}`}>
      <div className={`mx-auto px-4 sm:px-6 ${embed ? 'max-w-2xl' : 'max-w-3xl py-10 sm:py-16'} space-y-8`}>
        {children}
      </div>
    </div>
  );
}

/** Accent colour, favicon and custom CSS from the page's branding. */
function useBranding(data: PublicStatus | undefined) {
  useEffect(() => {
    if (!data) return;
    const root = document.documentElement;
    const prev = root.style.getPropertyValue('--status-accent');
    if (data.page.accent_color) root.style.setProperty('--status-accent', data.page.accent_color);

    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    const prevHref = link?.href ?? null;
    if (data.page.favicon_url) {
      if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
      link.href = data.page.favicon_url;
    }

    let style: HTMLStyleElement | null = null;
    if (data.page.custom_css) {
      style = document.createElement('style');
      style.setAttribute('data-status-page-css', '');
      style.textContent = data.page.custom_css;
      document.head.appendChild(style);
    }
    return () => {
      root.style.setProperty('--status-accent', prev);
      if (link && prevHref) link.href = prevHref;
      style?.remove();
    };
  }, [data]);
}

function PasscodePrompt({ onVerified }: { onVerified: () => void }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await verifyPagePasscode(code.trim());
      onVerified();
    } catch (err) {
      setError(apiError(err, 'That passcode was not accepted'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="max-w-sm mx-auto space-y-4">
      <div className="text-center">
        <h1 className="text-xl font-semibold">This page needs a passcode</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Enter the passcode you were given to see the status of these services.</p>
      </div>
      {error && <Alert tone="error">{error}</Alert>}
      <Input
        aria-label="Passcode"
        type="text"
        required
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        value={code}
        onChange={e => setCode(e.target.value)}
        className="font-mono tracking-widest text-center text-lg"
        placeholder="XXXX-XXXX-XXXX"
      />
      <Button type="submit" loading={busy} className="w-full" size="lg">{busy ? 'Checking…' : 'View status'}</Button>
    </form>
  );
}

function StatusPageBody({ data, embed }: { data: PublicStatus; embed: boolean }) {
  const tone = banner[data.overall];
  const accent = data.page.accent_color ?? undefined;

  const grouped = useMemo(() => {
    const top = data.services.filter(s => s.section_id === null);
    const sections = data.sections.map(sec => ({ ...sec, services: data.services.filter(s => s.section_id === sec.id) })).filter(sec => sec.services.length > 0);
    return { top, sections };
  }, [data]);

  const active = data.maintenance.filter(m => m.active);
  const upcoming = data.maintenance.filter(m => !m.active);
  const ongoing = data.incidents.filter(i => i.ongoing);
  const past = data.incidents.filter(i => !i.ongoing);

  return (
    <Shell embed={embed}>
      <header className="flex items-start gap-4">
        {data.page.logo_url && (
          <img src={data.page.logo_url} alt="" className={`${embed ? 'h-8' : 'h-12'} w-auto object-contain shrink-0`} />
        )}
        <div className="min-w-0">
          <h1 className={`${embed ? 'text-lg' : 'text-2xl'} font-bold tracking-tight`} style={accent ? { color: accent } : undefined}>{data.page.title}</h1>
          {!embed && data.page.description && <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{data.page.description}</p>}
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 tabular-nums">Updated {formatDateTime(data.generated_at)}</p>
        </div>
      </header>

      <div className={`rounded-lg border p-5 flex items-center gap-4 ${tone.band}`} role="status">
        <span className={`w-3 h-3 rounded-full shrink-0 ${statusColors[data.overall]}`} aria-hidden="true" />
        <h2 className="text-lg font-semibold tracking-tight">{tone.text}</h2>
      </div>

      {active.length > 0 && (
        <section aria-label="Maintenance in progress" className="rounded-lg border border-brand-200 dark:border-brand-900 bg-brand-50 dark:bg-brand-900/20 p-4 space-y-2">
          {active.map(m => (
            <div key={m.id} id={`maintenance-${m.id}`}>
              <p className="text-sm font-medium text-brand-900 dark:text-brand-200">Maintenance in progress: {m.name}</p>
              <p className="text-xs text-brand-800 dark:text-brand-300 mt-0.5">
                {m.ends_at ? `Until ${formatDateTime(m.ends_at)}` : m.schedule}
                {' · '}Affects {m.services.join(', ')}
              </p>
              {m.description && <p className="text-sm text-brand-800 dark:text-brand-300 mt-1">{m.description}</p>}
            </div>
          ))}
        </section>
      )}

      {!embed && ongoing.length > 0 && (
        <section aria-label="Ongoing incidents" className="space-y-3">
          {ongoing.map(i => <IncidentCard key={i.id} incident={i} highlight />)}
        </section>
      )}

      {!embed && data.announcements.length > 0 && (
        <section aria-label="Announcements" className="space-y-3">
          {data.announcements.map(a => (
            <article key={a.id} id={`announcement-${a.id}`} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
              <h3 className="font-medium">{a.title}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{formatDateTime(a.starts_at ?? a.created_at)}</p>
              {a.text && <p className="text-sm text-gray-700 dark:text-gray-300 mt-2 whitespace-pre-wrap">{a.text}</p>}
            </article>
          ))}
        </section>
      )}

      {!embed && upcoming.length > 0 && (
        <section aria-label="Scheduled maintenance" className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-3">
          <h3 className="text-sm font-semibold">Scheduled maintenance</h3>
          {upcoming.map(m => (
            <div key={m.id} id={`maintenance-${m.id}`} className="text-sm">
              <p className="font-medium">{m.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {m.schedule ? `${m.schedule} (${m.timezone})` : `${formatDateTime(m.starts_at)} – ${formatDateTime(m.ends_at)}`}
                {' · '}{m.services.join(', ')}
              </p>
              {m.description && <p className="text-gray-600 dark:text-gray-300 mt-1">{m.description}</p>}
            </div>
          ))}
        </section>
      )}

      {data.services.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No services are published on this page yet.</p>
      ) : (
        <div className="space-y-6">
          {grouped.top.length > 0 && <ServiceList services={grouped.top} embed={embed} showResponse={data.page.show_response_times} />}
          {grouped.sections.map(sec => (
            <section key={sec.id} aria-labelledby={`section-${sec.id}`} className="space-y-2">
              <div>
                <h2 id={`section-${sec.id}`} className="font-semibold">{sec.name}</h2>
                {sec.description && <p className="text-sm text-gray-500 dark:text-gray-400">{sec.description}</p>}
              </div>
              <ServiceList services={sec.services} embed={embed} showResponse={data.page.show_response_times} />
            </section>
          ))}
        </div>
      )}

      {!embed && (
        <section aria-label="Incident history" className="space-y-3">
          <h2 className="font-semibold">Incident history</h2>
          {past.length === 0 && ongoing.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No incidents reported.</p>
          ) : past.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No earlier incidents.</p>
          ) : (
            <IncidentTimeline incidents={past} />
          )}
        </section>
      )}

      <footer className="text-xs text-gray-400 dark:text-gray-500 text-center pt-4 space-y-1">
        {data.page.footer_text && <p className="text-gray-500 dark:text-gray-400">{data.page.footer_text}</p>}
        <p className="flex items-center justify-center gap-3 flex-wrap">
          {data.page.support_url && <a href={data.page.support_url} className="hover:underline">Support</a>}
          {!embed && <a href={data.links.feed} className="hover:underline" type="application/rss+xml">RSS feed</a>}
          {embed && <a href={data.links.page} target="_blank" rel="noreferrer" className="hover:underline">Full status page</a>}
          <span>Monitored with PulseCheck</span>
        </p>
      </footer>
    </Shell>
  );
}

function ServiceList({ services, embed, showResponse }: { services: PublicStatusService[]; embed: boolean; showResponse: boolean }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 overflow-hidden">
      {services.map(service => <StatusRow key={service.id} service={service} embed={embed} showResponse={showResponse} />)}
    </div>
  );
}

function cellClass(status: DailyCell['status']): string {
  switch (status) {
    case 'up': return 'bg-status-up';
    case 'degraded': return 'bg-status-degraded';
    case 'down': return 'bg-status-down';
    case 'maintenance': return 'bg-status-maintenance';
    default: return 'bg-gray-200 dark:bg-gray-700';
  }
}

function cellTitle(c: DailyCell): string {
  const day = parseServerDate(`${c.date}T00:00:00Z`);
  const label = day ? day.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' }) : c.date;
  if (c.uptime === null) return `${label}: no data`;
  const inc = c.incidents > 0 ? ` · ${c.incidents} incident${c.incidents === 1 ? '' : 's'}` : '';
  return `${label}: ${c.uptime}% uptime${inc}`;
}

function StatusRow({ service, embed, showResponse }: { service: PublicStatusService; embed: boolean; showResponse: boolean }) {
  const days = embed ? service.daily.slice(-30) : service.daily;
  return (
    <div className={`${embed ? 'px-3 py-2 space-y-1' : 'p-4 space-y-2'}`}>
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-medium">{service.name}</h3>
          {!embed && service.description && <p className="text-xs text-gray-500 dark:text-gray-400">{service.description}</p>}
        </div>
        <span className="flex items-center gap-1.5 text-sm">
          <span className={`w-2 h-2 rounded-full ${statusColors[service.status]}`} aria-hidden="true" />
          {statusLabels[service.status]}
        </span>
      </div>

      {/* Oldest → newest, newest at the right edge, grey where nothing was observed. */}
      <div
        className={`flex items-center gap-[2px] ${embed ? 'h-4' : 'h-6'}`}
        role="img"
        aria-label={service.uptime_90d === null
          ? `${service.name}: no uptime data yet`
          : `${service.name}: ${service.uptime_90d}% uptime over ${days.length} days`}
      >
        {days.map(c => (
          <div key={c.date} className={`flex-1 h-full rounded-sm ${cellClass(c.status)}`} title={cellTitle(c)} />
        ))}
      </div>

      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 tabular-nums flex-wrap gap-x-3">
        <span>{days.length} days ago</span>
        <span className="flex gap-3">
          {!embed && <span>24h · {formatUptime(service.uptime_24h)}</span>}
          {!embed && <span>7d · {formatUptime(service.uptime_7d)}</span>}
          <span>30d · {formatUptime(service.uptime_30d)}</span>
          {!embed && <span>90d · {formatUptime(service.uptime_90d)}</span>}
          {showResponse && service.avg_response_24h !== undefined && (
            <span>{service.avg_response_24h === null ? '—' : formatDuration(service.avg_response_24h)} avg</span>
          )}
        </span>
        <span>Today</span>
      </div>
    </div>
  );
}

function durationText(i: PublicIncident): string {
  const start = parseServerDate(i.started_at)?.getTime();
  const end = i.resolved_at ? parseServerDate(i.resolved_at)?.getTime() : Date.now();
  if (!start || !end) return '';
  const s = Math.max(0, Math.round((end - start) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} min`;
  return `${Math.floor(s / 86400)} d ${Math.floor((s % 86400) / 3600)} h`;
}

function IncidentCard({ incident, highlight = false }: { incident: PublicIncident; highlight?: boolean }) {
  const isDown = incident.status === 'down';
  return (
    <article
      id={`incident-${incident.id}`}
      className={`rounded-lg border p-4 ${highlight
        ? isDown ? 'border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20' : 'border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-900/20'
        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'}`}
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h3 className="font-medium flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${isDown ? 'bg-status-down' : 'bg-status-degraded'}`} aria-hidden="true" />
          {incident.title}
        </h3>
        <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
          {incident.ongoing ? `Ongoing · ${durationText(incident)}` : `Resolved · ${durationText(incident)}`}
        </span>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
        {incident.service_name} · {formatDateTime(incident.started_at)}{incident.resolved_at ? ` → ${formatTime(incident.resolved_at)}` : ''}
      </p>
      {incident.updates.length > 0 && (
        <ol className="mt-3 space-y-2 border-l-2 border-gray-200 dark:border-gray-700 pl-3">
          {[...incident.updates].reverse().map((u, i) => (
            <li key={i} className="text-sm">
              <p>
                {u.status && <span className="font-medium mr-1">{UPDATE_LABEL[u.status] ?? u.status} —</span>}{' '}
                {u.title}
              </p>
              {u.text && <p className="text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{u.text}</p>}
              <p className="text-xs text-gray-500 dark:text-gray-400">{formatDateTime(u.created_at)}</p>
            </li>
          ))}
        </ol>
      )}
    </article>
  );
}

function IncidentTimeline({ incidents }: { incidents: PublicIncident[] }) {
  const byDay = useMemo(() => {
    const map = new Map<string, PublicIncident[]>();
    for (const i of incidents) {
      const key = formatDate(i.started_at);
      map.set(key, [...(map.get(key) ?? []), i]);
    }
    return [...map.entries()];
  }, [incidents]);

  return (
    <div className="space-y-4">
      {byDay.map(([day, list]) => (
        <div key={day}>
          <h3 className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">{day}</h3>
          <div className="space-y-2">
            {list.map(i => <IncidentCard key={i.id} incident={i} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
